/**
 * @name: DataLayer Track
 * @author: AI Agent
 * @date: 2026-09-10
 * @description: GTM dataLayer events from Liquid markers and product JSON.
 * @dependencies: window._theme, #pdp_product_data, #variants_data, [data-product-json]
 * @strict-mode: true
 */
'use strict';

(() => {
  const DEBOUNCE_MS = 400;
  const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/;
  const EVENT_MARKER = '[data-datalayer-event]';
  const SKIP_CLICK_EVENTS = new Set([
    'popup_view',
    'newsletter_form_interaction',
    'out_of_stock_view',
    'out_of_stock_cta_click',
    'login',
    'sign_up',
  ]);
  const SKIP_SUBMIT_EVENTS = new Set(['out_of_stock_view', 'search_bar_click']);

  class DatalayerTrack {
    constructor() {
      this.lastEventKey = '';
      this.lastEventAt = 0;
      this.lastOutOfStockItem = null;
      this.viewedOutOfStockIds = new Set();
      this.outOfStockObserver = null;
      this.activePopupOpen = false;
      this.popupFormName = '';
      this._popupObserverBound = false;
      this.trackedPopupSteps = new Set();
      this.pdpProductData = null;
      this.pdpVariants = null;
    }

    init() {
      this.loadPdpJson();
      document.addEventListener('click', this.onClick.bind(this), true);
      document.addEventListener('submit', this.onSubmit.bind(this), true);
      document.addEventListener('focusin', this.onSearchInteract.bind(this), true);
      this.bindOutOfStockView();
      this.bindAutomaticPopupObserver();
      this.bindIframeMarkerClicks();
      this.bindSideCartAuto();
      this.bindSearchMenu();
      this.bindKlarna();
      this.bindPdpVideo();
    }

    onClick(event) {
      const target = this.getEventTarget(event);
      if (!target) return;

      if (target.closest('#ltkpopup-submit, #ltkpopup-mobile-submit, .ltkpopup-subscribe')) {
        this.trackPopupFormSubmit(target);
      }

      const notifyCta = target.closest('[data-notify-modal-open], .sold_notify_me');
      if (notifyCta) {
        this.trackOutOfStockCtaClick(notifyCta);
        return;
      }

      const marker = target.closest(EVENT_MARKER);
      if (!marker) return;
      if (marker.matches('klarna-placement')) return;

      const parsed = this.parseMarker(marker);
      if (!parsed.event) return;
      if (SKIP_CLICK_EVENTS.has(parsed.event) && !this.isAuthFormSubmit(parsed.event, marker)) return;

      const fields = this.buildFields(parsed.event, marker, target, parsed.sectionName);
      if (!fields) return;

      this.pushEvent(parsed.event, fields);

      const popupName = parsed.event === 'announcement_bar_interaction' && marker.getAttribute('data-popup-name');
      if (popupName) {
        this.popupFormName = popupName;
        this.activePopupOpen = true;
        this.pushPopupView(parsed.sectionName);
      }
    }

    onSearchInteract(event) {
      const target = this.getEventTarget(event);
      if (!(target instanceof HTMLInputElement) || target.getAttribute('id') !== 'header_search') return;

      const marker = target.closest(EVENT_MARKER);
      if (!marker) return;

      const parsed = this.parseMarker(marker);
      if (parsed.event !== 'search_bar_click') return;

      this.pushEvent('search_bar_click', {});
    }

    onSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      if (form.id === 'ltkpopup-form' || form.querySelector('#ltkpopup-email, #ltkpopup-mobile')) {
        this.trackPopupFormSubmit(event.submitter);
      }

      const submitter = event.submitter instanceof Element ? event.submitter : form;
      const marker = submitter.closest(EVENT_MARKER) || form.querySelector(EVENT_MARKER);
      if (!marker) return;

      const parsed = this.parseMarker(marker);
      if (!parsed.event || SKIP_SUBMIT_EVENTS.has(parsed.event)) return;

      const fields = this.buildFields(parsed.event, marker, form, parsed.sectionName);
      if (!fields) return;

      this.pushEvent(parsed.event, fields);
    }

    isAuthFormSubmit(eventName, marker) {
      if (eventName !== 'login' && eventName !== 'sign_up') return false;
      if (!marker.matches('[type="submit"]')) return false;

      const form = marker.closest('form');
      return Boolean(form && typeof form.checkValidity === 'function' && form.checkValidity());
    }

    pushEvent(eventName, fields = {}) {
      const eventKey = [
        eventName,
        fields.click_text,
        fields.filter_value,
        fields.sort_value,
        fields.interaction_type,
        fields.section,
        fields.step,
      ]
        .filter(Boolean)
        .join('|');

      const now = Date.now();
      if (eventKey && eventKey === this.lastEventKey && now - this.lastEventAt < DEBOUNCE_MS) return;
      this.lastEventKey = eventKey;
      this.lastEventAt = now;

      const eventFields = this.fillNa({ ...fields });
      delete eventFields._ecommerce;

      const data = {
        event: eventName,
        ...this.getPageContext(),
        ...eventFields,
      };

      if (fields._ecommerce) data.ecommerce = this.fillNa(fields._ecommerce);

      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(data);
      } catch (error) {
        console.error('DataLayer push failed:', error);
      }
    }

    trackPopupFormSubmit(target) {
      // Prefer the last clicked popup name; fall back to the currently visible [data-popup-name] element
      const namedVisible = Array.from(document.querySelectorAll('[data-popup-name]')).find(
        (el) => !el.hidden && !el.classList.contains('hidden') && el.getClientRects().length > 0
      );
      const named = namedVisible || document.querySelector('[data-popup-name]');
      const formName = this.orNa(
        this.popupFormName || (named && named.getAttribute('data-popup-name')) || this.getPopupFormName()
      );
      const email = document.getElementById('ltkpopup-email');
      const mobile = document.getElementById('ltkpopup-mobile');
      const isStep2 = Boolean(
        (target && target.closest && target.closest('#ltkpopup-mobile-submit')) ||
          (mobile && mobile.getClientRects().length && !(email && email.getClientRects().length))
      );

      if (isStep2) {
        if (!this.isPopupFieldValid(mobile)) return;

        this.pushEvent('popup_form_submit', {
          form_name: formName,
          form_location: 'Popup',
          step: '2',
          form_status: 'phone number',
        });
        return;
      }

      if (!this.isPopupFieldValid(email)) return;

      this.pushEvent('popup_form_submit', {
        form_name: formName,
        form_location: 'Popup',
        step: '1',
        form_status: 'email',
      });

      this.pushPopupView('Popup', '2', 'phone number');
    }

    isPopupFieldValid(input) {
      if (!input || !input.getClientRects().length) return false;

      const value = this.normalizeText(input.value);
      if (!value) return false;

      const pattern = input.getAttribute('pattern');
      if (pattern) {
        try {
          if (new RegExp(pattern).test(value)) return true;
        } catch (error) {}
      }

      if (input.getAttribute('type') === 'email' || input.getAttribute('id') === 'ltkpopup-email') {
        return EMAIL_REGEX.test(value);
      }

      return value.replace(/\D/g, '').length === 10;
    }

    getPopupFormName(container) {
      const root = container || document;
      const line1 = (
        root.querySelector('#ltkpopup-contents .ltkpopup-line-1, .ltkpopup-line-1') ||
        document.querySelector('#ltkpopup-contents .ltkpopup-line-1')
      )?.textContent?.trim() || '';

      const line2 = (
        root.querySelector('#ltkpopup-contents .ltkpopup-line-2, .ltkpopup-line-2') ||
        document.querySelector('#ltkpopup-contents .ltkpopup-line-2')
      )?.textContent?.trim() || '';

      const dynamicText = [line1, line2].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

      return dynamicText ? `Perfumania - ${dynamicText} - Entry Email - SMS` : '';
    }

    pushPopupView(location = 'Popup', step = '1', formStatus = 'email') {
      const formName = this.popupFormName || this.getPopupFormName();
      const stepKey = `${formName || 'default'}_${step}`;
      if (this.trackedPopupSteps && this.trackedPopupSteps.has(stepKey)) return;
      if (this.trackedPopupSteps) this.trackedPopupSteps.add(stepKey);

      this.pushEvent('popup_view', {
        form_name: this.orNa(formName),
        form_location: this.orNa(location),
        step: String(step),
        form_status: this.orNa(formStatus),
      });
    }

    bindAutomaticPopupObserver() {
      if (this._popupObserverBound) return;
      this._popupObserverBound = true;

      const ID = 'ltkpopup-container';
      let contentMo = null;

      const isContainerVisible = (el) => {
        if (!el || !(el instanceof Element)) return false;
        if (el.hidden || el.classList.contains('hidden')) return false;
        if (el.offsetWidth === 0 && el.offsetHeight === 0 && !el.getClientRects().length) {
          return false;
        }
        try {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden';
        } catch (e) {
          return false;
        }
      };

      const getPopupStepInfo = (container) => {
        const email = container.querySelector('#ltkpopup-email, input[type="email"]');
        const mobile = container.querySelector(
          '#ltkpopup-mobile, input[type="tel"], input[name*="mobile" i], input[name*="phone" i], #ltkpopup-mobile-submit'
        );

        const isEmailVisible = isContainerVisible(email);
        const isMobileVisible = isContainerVisible(mobile);

        if (isMobileVisible && !isEmailVisible) {
          return { step: '2', form_status: 'phone number' };
        }
        return { step: '1', form_status: 'email' };
      };

      const track = (container) => {
        if (!isContainerVisible(container)) return false;

        const formName = this.getPopupFormName(container) || this.popupFormName;
        if (!formName) return false;

        this.activePopupOpen = true;
        this.popupFormName = formName;

        const { step, form_status } = getPopupStepInfo(container);
        this.pushPopupView('Popup', step, form_status);
        return true;
      };

      const onPopupRemoved = () => {
        this.activePopupOpen = false;
        this.popupFormName = '';
        if (this.trackedPopupSteps) this.trackedPopupSteps.clear();
        if (contentMo) {
          contentMo.disconnect();
          contentMo = null;
        }
      };

      const onPopupAdded = (container) => {
        track(container);

        if (!contentMo) {
          contentMo = new MutationObserver(() => {
            if (isContainerVisible(container)) {
              track(container);
            } else if (this.activePopupOpen) {
              onPopupRemoved();
            }
          });
          contentMo.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class', 'hidden'],
          });
        }
      };

      const findPopupContainer = (node) => {
        if (!node || node.nodeType !== 1) return null;
        if (node.id === ID) return node;
        return typeof node.querySelector === 'function' ? node.querySelector(`#${ID}`) : null;
      };

      new MutationObserver((muts) => {
        for (const m of muts) {
          for (const n of m.addedNodes) {
            const found = findPopupContainer(n);
            if (found) onPopupAdded(found);
          }
          for (const n of m.removedNodes) {
            const found = findPopupContainer(n);
            if (found) onPopupRemoved();
          }
        }
      }).observe(document.body, { childList: true });

      const existing = document.getElementById(ID);
      if (existing) onPopupAdded(existing);

      document.addEventListener(
        'click',
        (e) => {
          if (e.target.closest?.('.ltkpopup-close, #ltkpopup-close, [data-ltkpopup-close]')) {
            onPopupRemoved();
          }
        },
        true
      );
    }

    pushFromMarker(eventName, fields = {}) {
      const marker = this.findMarker(eventName);
      if (!marker) return;

      const parsed = this.parseMarker(marker);
      const fromMarker = this.buildFields(parsed.event, marker, marker, parsed.sectionName) || {};
      this.pushEvent(eventName, { ...fromMarker, ...fields });
    }

    resolveOutOfStockVariant(product, cardOrBadge, explicitVariantId) {
      const variants = (product && product.variants) || [];

      if (explicitVariantId && variants.length) {
        const match = variants.find((variant) => String(variant.id) === String(explicitVariantId));
        if (match) return match;
      }

      const card =
        cardOrBadge && typeof cardOrBadge.closest === 'function'
          ? cardOrBadge.closest('product-card, .product-card, .product-item')
          : null;

      const contextVariantId =
        (card && card.querySelector('[data-variant-id]')?.getAttribute('data-variant-id')) ||
        (card && card.querySelector('[name="id"]')?.value);

      if (contextVariantId && variants.length) {
        const match = variants.find((variant) => String(variant.id) === String(contextVariantId));
        if (match) return match;
      }

      const foundVariant = this.findVariant(cardOrBadge, cardOrBadge);
      if (foundVariant) {
        if (variants.length) {
          const match = variants.find((variant) => String(variant.id) === String(foundVariant.id));
          if (match) return match;
        } else {
          return foundVariant;
        }
      }

      if (variants.length === 1) {
        return variants[0];
      }

      return null;
    }
    initOutOfStockObserver() {
      if (this.outOfStockObserver) return this.outOfStockObserver;
      if (typeof window.IntersectionObserver !== 'function') return null;

      this.outOfStockObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const target = entry.target;
            if (this.outOfStockObserver) {
              this.outOfStockObserver.unobserve(target);
            }

            const payload = target._outOfStockPayload;
            if (!payload) return;

            const { product, variant, listEl, key } = payload;
            delete target._outOfStockPayload;
            delete target._outOfStockKey;

            if (!key || this.viewedOutOfStockIds.has(key)) return;
            this.viewedOutOfStockIds.add(key);

            const item = this.buildItem(product, variant, this.getItemList(listEl));
            item.stock_status = 'out_of_stock';

            try {
              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({ ecommerce: null });
              window.dataLayer.push({
                event: 'out_of_stock_view',
                stock_status: 'out_of_stock',
                items: [this.fillNa(item)],
                ...this.getPageContext(),
              });
            } catch (e) {
              console.error('DataLayer out_of_stock_view push failed:', e);
            }
          });
        },
        { threshold: 0.2 }
      );

      return this.outOfStockObserver;
    }

    trackOutOfStockView() {
      if (!this.pdpProductData || !this.pdpVariants) {
        this.loadPdpJson();
      }

      const observer = this.initOutOfStockObserver();

      const registerOutOfStock = (product, variant, listEl, targetEl) => {
        const item = this.buildItem(product, variant, this.getItemList(listEl));
        item.stock_status = 'out_of_stock';
        if (!item.product_id || item.product_id === 'NA') return;
        const key =
          item.variant_id && item.variant_id !== 'NA'
            ? `${item.product_id}_${item.variant_id}`
            : item.product_id;
        if (!key || this.viewedOutOfStockIds.has(key)) return;

        // Fallback if IntersectionObserver is not supported
        if (!observer) {
          this.viewedOutOfStockIds.add(key);
          try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
              event: 'out_of_stock_view',
              stock_status: 'out_of_stock',
              items: [this.fillNa(item)],
              ...this.getPageContext(),
            });
          } catch (e) {
            console.error('DataLayer out_of_stock_view push failed:', e);
          }
          return;
        }

        // Avoid re-observing if already scheduled for the same product/variant
        if (targetEl._outOfStockKey === key) return;

        targetEl._outOfStockPayload = { product, variant, listEl, key };
        targetEl._outOfStockKey = key;
        observer.observe(targetEl);
      };

      const badges = document.querySelectorAll('.product-card__badge.badge--sold-out, .badge--sold-out');
      if (badges.length > 0) {
        badges.forEach((badge) => {
          if (badge.hidden || badge.classList.contains('hidden')) return;

          const card = badge.closest('product-card, .product-card, .product-item');
          const badgesComponent = badge.closest('product-badges-component');
          const product =
            (card && this.findProduct(card)) ||
            (badgesComponent && this.parseJson(badgesComponent.querySelector('script[data-product-badges-json]')?.textContent)) ||
            this.findProduct(badge) ||
            this.pdpProductData;

          if (badgesComponent && product && (!product.variants || !product.variants.length)) {
            const badgeData = this.parseJson(badgesComponent.querySelector('script[data-product-badges-json]')?.textContent);
            if (badgeData && badgeData.variants) product.variants = badgeData.variants;
          }

          if (!this.hasProduct(product)) return;

          const explicitVariantId =
            (badgesComponent &&
              (badgesComponent.selectedVariantId || badgesComponent.getAttribute('data-selected-variant-id'))) ||
            null;

          let variant = this.resolveOutOfStockVariant(product, card || badge, explicitVariantId);

          // Never drop multi-variant cards — fallback to first out-of-stock variant
          if (!variant && product.variants && product.variants.length > 1) {
            variant = product.variants.find((v) => v.available === false) || product.variants[0];
          }

          const targetEl = card || badge;
          registerOutOfStock(product, variant, badge, targetEl);
        });
        return;
      }

      const form = this.findMarker('out_of_stock_view');
      if (!form || form.hidden || form.classList.contains('hidden')) return;

      const product = this.findProduct(form) || this.pdpProductData;
      if (!this.hasProduct(product) || product.available === true) return;

      const variant = this.resolveOutOfStockVariant(product, form, null);
      const targetEl = form.closest('.product, .main-product, product-info') || form;
      registerOutOfStock(product, variant, form, targetEl);
    }

    bindOutOfStockView() {
      if (this.isOutOfStockBound) return;
      this.isOutOfStockBound = true;

      this.trackOutOfStockView();

      if (window.customElements && typeof window.customElements.whenDefined === 'function') {
        window.customElements.whenDefined('product-badges-component').then(() => {
          this.trackOutOfStockView();
        });
      }

      this._onOutOfStockChange = (event) => {
        if (
          !event.type.startsWith('variant') &&
          !(event.target?.matches('input[type="radio"], select, [name="id"]') ||
            event.target?.closest('product-form, variant-radios, variant-selects'))
        ) return;
        this.trackOutOfStockView();
      };

      document.addEventListener('variant:changed', this._onOutOfStockChange);
      document.addEventListener('variant-picker-component:change', this._onOutOfStockChange);
      document.addEventListener('change', this._onOutOfStockChange);
    }

    unbindOutOfStockView() {
      if (!this.isOutOfStockBound) return;

      if (this.outOfStockObserver) {
        this.outOfStockObserver.disconnect();
        this.outOfStockObserver = null;
      }

      if (this._onOutOfStockChange) {
        document.removeEventListener('variant:changed', this._onOutOfStockChange);
        document.removeEventListener('variant-picker-component:change', this._onOutOfStockChange);
        document.removeEventListener('change', this._onOutOfStockChange);
      }

      this.isOutOfStockBound = false;
    }

    getOutOfStockCtaLocation(button) {
      if (button && button.closest('quickview-dialog, [data-quickview]')) return 'quick view';
      const path = window.location.pathname;
      if (path.includes('/products/')) return 'pdp page';
      if (path.includes('/collections')) return 'collection page';
      if (path.includes('/search')) return 'search page';
      if (path === '/') return 'home page';
      return 'other';
    }

    trackOutOfStockCtaClick(button) {
      const card = button.closest('product-card, .product-card, .product-item');
      const product = this.findProduct(button) || (card && this.findProduct(card)) || this.pdpProductData;
      const explicitVariantId = button.getAttribute('offer-var-id') || button.getAttribute('data-variant-id');
      const variant = this.resolveOutOfStockVariant(product, card || button, explicitVariantId);
      const item = this.buildItem(product, variant, this.getItemList(button));
      item.stock_status = 'out_of_stock';

      this.lastOutOfStockItem = item;
      const modal =
        (button && button.closest('notify-me')?.querySelector('[data-notify-me-modal]')) ||
        document.querySelector('[data-notify-me-modal]:not([style*="display: none"])');
      if (modal) modal._outOfStockItem = item;

      this.pushEvent('out_of_stock_cta_click', {
        click_text: this.normalizeText(button.innerText || button.textContent) || 'Out of Stock - Notify Me',
        cta_location: this.getOutOfStockCtaLocation(button),
        items: [item],
      });
    }

    bindSideCartAuto() {
      const original = window._theme && window._theme.toggleSideCart;
      if (typeof original !== 'function') return;

      window._theme.toggleSideCart = (show, callback, isAutoAction) => {
        const opening = Boolean(show) && !document.documentElement.classList.contains('cart-show');
        const result = original.call(window._theme, show, callback, isAutoAction);

        if (opening && isAutoAction) {
          this.pushEvent('side_cart_interaction', { interaction_type: 'auto' });
        }

        return result;
      };
    }

    bindPdpVideo() {
      if (!window.location.pathname.includes('/products/')) return;

      const track = (target, action) => {
        const video = target && (target.matches('video') ? target : target.querySelector('video'));
        if (!video) return;

        const product = this.pdpProductData || this.findProduct(video);
        if (!product) return;

        const variant = this.findVariant(video, video) || (product.variants && product.variants[0]);
        const item = this.buildItem(product, variant, this.getItemList(video));

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'pdp_video_interaction',
          video_name: product.title || 'Product Video',
          video_action: action,
          video_location: product.type || 'NA',
          items: [this.fillNa(item)],
          ...this.getPageContext(),
        });
      };

      document.addEventListener('play', (e) => track(e.target, 'start'), true);
      document.addEventListener('ended', (e) => track(e.target, 'complete'), true);
    }

    bindSearchMenu() {
      const track = this;
      const push = (type, url) => {
        track.pushEvent('search_menu_interaction', {
          interaction_type: type,
          click_text: 'Search Menu',
          click_url: url || '',
        });
      };

      customElements.whenDefined('header-search').then(() => {
        const proto = customElements.get('header-search').prototype;
        const events = { open: 'open', close: 'close', onChange: 'interact' };

        for (const [method, type] of Object.entries(events)) {
          const original = proto[method];
          proto[method] = function () {
            const wasOpen = this.isOpen;
            const result = original.apply(this, arguments);
            const shouldPush = type === 'interact' ? this.isOpen : wasOpen !== this.isOpen;
            if (shouldPush) push(type);
            return result;
          };
        }
      });

      document.addEventListener(
        'click',
        (event) => {
          const target = track.getEventTarget(event);
          if (!target || !document.body.classList.contains('search-active')) return;

          const root = target.closest('header-search');
          if (!root || target.closest('#header_search, [type="reset"], [data-close-search]')) return;

          push('interact', track.getClickUrl(root, target));
        },
        true
      );
    }

    bindKlarna() {
      document.addEventListener(
        'click',
        (event) => {
          const path = event.composedPath();
          if (!path.some((node) => node.getAttribute && node.getAttribute('part') === 'osm-cta')) return;

          const marker = path.find((node) => node.matches && node.matches('klarna-placement[data-datalayer-event]'));
          if (!marker) return;

          const fields = this.buildFields('pdp_interaction', marker, marker, 'Klarna');
          if (fields) this.pushEvent('pdp_interaction', fields);
        },
        true
      );
    }

    bindIframeMarkerClicks() {
      window.addEventListener('blur', () => {
        window.setTimeout(() => {
          const iframe = document.activeElement;
          if (!(iframe instanceof HTMLIFrameElement)) return;

          const marker = iframe.closest(EVENT_MARKER);
          if (!marker) return;

          const parsed = this.parseMarker(marker);
          if (parsed.event !== 'announcement_bar_interaction') return;

          const fields = this.buildFields(parsed.event, marker, marker, parsed.sectionName);
          if (!fields) return;

          this.pushEvent(parsed.event, fields);
        }, 0);
      });
    }

    buildFields(eventName, marker, target, sectionName) {
      const itemList = this.getItemList(marker);
      const clickText = this.getClickText(marker, target);
      const clickUrl = this.getClickUrl(marker, target);

      if (eventName === 'select_item') return this.selectItemFields(marker, itemList);

      if (eventName === 'add_to_wishlist') {
        if (marker.getAttribute('aria-pressed') === 'true' || marker.classList.contains('whislist-added')) return null;

        const fields = this.selectItemFields(marker, itemList);
        if (!fields) return null;

        fields._ecommerce.value = fields._ecommerce.items[0].price;
        return fields;
      }

      if (eventName === 'pdp_interaction') return this.pdpFields(marker, target, sectionName, clickText);
      if (eventName === 'out_of_stock_form_submit') return this.outOfStockSubmitFields(marker, target, clickText);
      if (eventName === 'search_bar_click') return {};

      if (eventName === 'filter_interaction') return this.filterFields(marker, sectionName, clickText);

      if (eventName === 'sort_interaction') {
        if (!clickText) return null;

        return { sort_value: clickText };
      }

      if (eventName === 'cta_interaction') {
        return {
          click_text: clickText,
          cta_position: itemList.name !== 'NA' ? itemList.name : this.orNa(sectionName),
          click_url: clickUrl,
        };
      }

      if (eventName === 'carousel_navigation') return this.carouselFields(marker, sectionName);

      if (eventName === 'header_menu_interaction') return this.headerFields(marker, target, sectionName);

      if (eventName === 'side_cart_interaction') {
        return {
          click_text: clickText,
          interaction_type: this.orNa(sectionName || clickText),
        };
      }

      if (eventName === 'footer_interaction') {
        return {
          click_text: clickText,
          section: this.orNa(sectionName),
          sub_category: 'NA',
        };
      }

      if (eventName === 'social_icon_interaction') {
        return {
          social_platform: this.orNa(clickText),
          click_text: this.orNa(clickText),
          social_location: this.orNa(sectionName),
          click_url: clickUrl,
        };
      }

      if (eventName === 'announcement_bar_interaction') {
        return { click_text: clickText, click_url: clickUrl };
      }

      if (eventName === 'video_interaction') return this.videoFields(marker, itemList, sectionName);
      if (eventName === 'sign_up' || eventName === 'login') return { method: this.orNa(sectionName) };

      if (eventName === 'popup_view') {
        const email = document.getElementById('ltkpopup-email');
        const mobile = document.getElementById('ltkpopup-mobile');
        const isStep2 = Boolean(
          (target && typeof target.closest === 'function' && target.closest('#ltkpopup-mobile-submit')) ||
            (mobile && mobile.getClientRects().length && !(email && email.getClientRects().length))
        );

        return {
          form_name: this.orNa(clickText),
          form_location: this.orNa(sectionName),
          step: isStep2 ? '2' : '1',
          form_status: isStep2 ? 'phone number' : 'email',
        };
      }

      if (eventName === 'newsletter_form_interaction') {
        return this.formFields(marker, target, sectionName, clickText);
      }

      if (eventName === 'back_to_top_interaction' || eventName === 'chatbot_interaction') return {};

      return { click_text: clickText, click_url: clickUrl };
    }

    selectItemFields(marker, itemList) {
      const product = this.findProduct(marker);
      if (!this.hasProduct(product)) return null;

      const item = this.buildItem(product, null, itemList);
      item.index = this.getCardIndex(marker);

      return {
        _ecommerce: {
          currency: this.getCurrency(),
          item_list_id: itemList.id,
          item_list_name: itemList.name,
          items: [item],
        },
      };
    }

    pdpFields(marker, target, sectionName, clickText) {
      const product = this.pdpProductData || this.findProduct(marker);
      if (!this.hasProduct(product)) return null;

      const item = this.buildItem(product, this.findVariant(target, marker), this.getItemList(marker));
      const quantityRoot = marker.closest('product-form') || marker;
      const quantityInput = quantityRoot.querySelector('[name="quantity"]');
      if (quantityInput) item.quantity = Number(quantityInput.value) || 1;

      return {
        click_text: clickText,
        product_name: product.title || 'NA',
        category: this.orNa(product.type),
        section: this.orNa(sectionName),
        items: [item],
      };
    }

    carouselFields(marker, sectionName) {
      const named = marker.closest('[data-item-list-name]') || marker;

      return {
        interaction_type: this.orNa(this.normalizeText(sectionName).toLowerCase()),
        carousel_name: this.orNa(named.getAttribute('data-item-list-name')),
        section: this.getPageSection(),
      };
    }

    getPageSection() {
      const template = this.normalizeText(document.body.getAttribute('data-template')).toLowerCase();
      const path = window.location.pathname || '/';

      if (path === '/' || template === 'index' || template.indexOf('index.') === 0) return 'Homepage';
      if (path.indexOf('/cart') === 0 || template.indexOf('cart') === 0) return 'Cart';
      if (path.indexOf('/search') === 0 || template.indexOf('search') === 0) return 'Search';
      if (path.indexOf('/products/') !== -1 || template.indexOf('product') === 0) return 'Product';
      if (path.indexOf('/collections/') !== -1 || template.indexOf('collection') === 0) return 'Collection';
      if (template.indexOf('blog') === 0 || template.indexOf('article') === 0) return 'Blog';

      return 'Page';
    }

    filterFields(marker, sectionName, clickText) {
      const filterName = this.getFilterName(marker);
      const isApply = this.normalizeText(sectionName).toLowerCase() === 'apply';
      const filterValue = isApply ? this.getPriceFilterValue(marker) || clickText : clickText;

      if (!filterValue) return null;
      if (!isApply && this.normalizeText(filterValue) === this.normalizeText(filterName)) return null;

      return {
        filter_name: this.orNa(filterName),
        filter_value: this.orNa(filterValue),
        interaction_type: this.orNa(sectionName),
      };
    }

    outOfStockSubmitFields(marker, target, clickText) {
      const root = marker.closest('[data-notify-me-modal], [data-backinstockform]') || marker;
      const email = root.querySelector('input[type="email"]');
      if (email && !EMAIL_REGEX.test(this.normalizeText(email.value))) return null;

      let item = root._outOfStockItem || this.lastOutOfStockItem;
      if (!item) {
        const product = this.findProduct(root) || this.pdpProductData;
        const variant = this.resolveOutOfStockVariant(product, root, null);
        item = this.buildItem(product, variant, this.getItemList(marker));
        if (item) item.stock_status = 'out_of_stock';
      }

      return {
        click_text: clickText,
        stock_status: 'out_of_stock',
        items: item ? [this.fillNa(item)] : [],
      };
    }

    headerFields(marker, target, sectionName) {
      const node = target || marker;
      const banner = node.closest('[data-nav-banner]');
      const lv1 = node.closest('[data-nav-lv1]');
      const lv2 = node.closest('[data-nav-lv2]');
      const lv3 = node.closest('[data-nav-lv3]');
      const heading = lv2 && (lv2.querySelector('[data-nav-heading]') || lv2.querySelector('[role="heading"]'));
      const bannerCol = banner && banner.closest('[data-nav-banner-col]');
      const clickText = banner
        ? this.getVisibleText(banner.querySelector('[data-nav-title]')) || this.normalizeText(banner.getAttribute('aria-label'))
        : this.getClickText(marker, target);

      let subCategory = this.normalizeText(marker.getAttribute('data-sub-category'));
      if (banner) subCategory = this.getVisibleText(bannerCol && bannerCol.querySelector('[data-nav-banner-head]'));
      else if (lv3 || lv2) subCategory = this.getVisibleText(heading) || clickText;

      return {
        click_text: this.orNa(clickText),
        section: this.orNa(sectionName),
        main_menu: this.orNa(
          this.normalizeText(lv1 && lv1.getAttribute('data-main-menu')) ||
            this.normalizeText(marker.getAttribute('data-main-menu')) ||
            clickText
        ),
        sub_category: this.orNa(subCategory),
      };
    }

    videoFields(marker, itemList, sectionName) {
      const video = this.getVideo(marker);
      if (!video) return null;

      const actionKey = this.normalizeText(sectionName).toLowerCase();
      let action = video.paused ? 'start' : 'pause';
      if (actionKey === 'mute') action = video.muted ? 'unmute' : 'mute';
      if (actionKey === 'fullscreen') action = 'fullscreen';
      if (actionKey === 'play') action = video.paused ? 'start' : 'pause';

      return {
        video_name: this.orNa(video.getAttribute('aria-label') || video.getAttribute('title') || itemList.name),
        video_action: action,
        video_location: itemList.name,
        video_position: itemList.name,
      };
    }

    formFields(marker, target, sectionName, clickText) {
      const root = (target && target.closest('form')) || (marker && marker.closest('form')) || marker;
      const email = root && root.querySelector('input[type="email"], #ltkpopup-email');
      if (email && !EMAIL_REGEX.test(this.normalizeText(email.value))) return null;

      return {
        form_name: this.orNa(clickText),
        form_location: this.orNa(sectionName),
      };
    }

    findProduct(el) {
      if (!el) return this.pdpProductData;

      const notify = el.closest('[data-notify-me-modal], [data-backinstockform], notify-me');
      if (notify) {
        const script = notify.querySelector(
          'script[type="application/json"][data-notify-product], script[type="application/json"][data-product-json]'
        );
        const parsed = this.parseJson(script && script.textContent);
        if (parsed) return parsed;
      }

      const card = el.closest('product-card, .product-item, [data-product-id]') || el;
      const scripts = card.querySelectorAll(
        'script[type="application/json"][data-product-json], script[data-product-json]'
      );

      for (const script of scripts) {
        const parsed = this.parseJson(script.textContent);
        if (this.hasProduct(parsed)) return parsed;
      }

      return this.pdpProductData;
    }

    findVariant(el, marker) {
      if (!this.pdpVariants || !this.pdpVariants.length) return null;

      const radio = this.getVariantRadio(el);
      if (radio) {
        const variantId = radio.value || radio.getAttribute('data-variant-id');
        const match = this.pdpVariants.find((variant) => String(variant.id) === String(variantId));
        if (match) return match;
      }

      const root = (marker && marker.closest('product-form')) || marker || el;
      const hiddenId = root && root.querySelector('[name="id"]');
      const currentId = hiddenId && (hiddenId.value || hiddenId.getAttribute('data-vid'));
      if (!currentId) return this.pdpVariants[0];

      return this.pdpVariants.find((variant) => String(variant.id) === String(currentId)) || this.pdpVariants[0];
    }

    buildItem(product, variant, itemList) {
      const prod = product || {};
      const productId = prod.id || prod.product_id || '';
      const variantData = variant || {};
      const variantId =
        variantData.id || variantData.variant_id || prod.variant_id || prod.selected_variant_id || '';
      const compareAt = this.toMoney(
        variantData.compare_at_price || prod.compare_at_price || prod.compareAtPrice || 0,
        Boolean(variantData.id)
      );
      const price = this.toMoney(
        variantData.price != null ? variantData.price : prod.price != null ? prod.price : prod.final_price,
        Boolean(variantData.id)
      );
      const discount =
        compareAt > price
          ? Number((compareAt - price).toFixed(2))
          : this.toMoney(prod.discount || prod.total_discount || 0);
      const available = variantData.available != null ? Boolean(variantData.available) : prod.available !== false;

      return {
        item_id: `shopify_${this.getCountryCode()}_${productId}_${variantId}`,
        product_id: String(productId),
        variant_id: String(variantId),
        item_name: prod.title || prod.item_name || prod.product_title || 'NA',
        quantity: Number(prod.quantity || 1),
        price,
        discount,
        item_brand: prod.vendor || prod.item_brand || 'NA',
        item_list_name: itemList ? itemList.name : 'NA',
        item_list_id: itemList ? itemList.id : 'NA',
        stock_status: available ? 'in_stock' : 'out_of_stock',
      };
    }

    getItemList(el) {
      const marker = el && el.closest('[data-item-list-id], [data-item-list-name]');

      return {
        id: this.orNa(marker && marker.getAttribute('data-item-list-id')),
        name: this.orNa(marker && marker.getAttribute('data-item-list-name')),
      };
    }

    getCardIndex(card) {
      if (!card) return 0;

      const item = card.closest('slideshow-container-item') || card;
      const parent = item.parentElement;
      if (!parent) return 0;

      const index = Array.prototype.indexOf.call(parent.children, item);
      return index >= 0 ? index : 0;
    }

    parseMarker(el) {
      const raw = this.normalizeText((el && el.getAttribute('data-datalayer-event')) || '');
      const parts = raw.split(',').map((part) => part.trim()).filter(Boolean);

      return { event: parts[0] || '', sectionName: parts.slice(1).join(', ') };
    }

    findMarker(eventName) {
      const nodes = document.querySelectorAll(EVENT_MARKER);
      for (const node of nodes) {
        if (this.parseMarker(node).event === eventName) return node;
      }

      return null;
    }

    parseJson(text) {
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch (error) {
        return null;
      }
    }

    loadPdpJson() {
      const productNode = document.getElementById('pdp_product_data');
      const variantNode = document.getElementById('variants_data');
      this.pdpProductData = this.parseJson(productNode && productNode.textContent);
      this.pdpVariants = this.parseJson(variantNode && variantNode.textContent);
    }

    hasProduct(product) {
      return Boolean(
        product && (product.id || product.product_id || product.title || product.product_title || product.handle)
      );
    }

    getFilterName(marker) {
      const widget = marker && marker.closest('[data-title]');
      const fromTitle = this.normalizeText(widget && widget.getAttribute('data-title'));
      if (fromTitle) return fromTitle;

      const priceWrap = marker && marker.closest('[data-value]');
      return this.normalizeText(priceWrap && priceWrap.getAttribute('data-value'));
    }

    getPriceFilterValue(marker) {
      const root = (marker && marker.closest('[data-value], [data-title]')) || marker;
      if (!root) return '';

      const checked = root.querySelector('input[type="radio"]:checked');
      if (checked) {
        const checkedId = checked.getAttribute('id');
        const label = checkedId ? document.querySelector(`label[for="${checkedId}"]`) : null;
        return this.getVisibleText(label) || this.normalizeText(checked.getAttribute('aria-label') || checked.value);
      }

      const minInput = root.querySelector('[data-price-min-input]');
      const maxInput = root.querySelector('[data-price-max-input]');
      const minValue = this.normalizeText(minInput && minInput.value);
      const maxValue = this.normalizeText(maxInput && maxInput.value);
      if (!minValue && !maxValue) return '';

      return `${minValue || '0'} - ${maxValue || ''}`.trim();
    }

    getSearchQuery(marker) {
      const root = (marker && marker.closest('form')) || marker;
      if (!root) return '';

      const input = root.querySelector('input[type="search"], [name="searches"], [name="q"]');
      return this.normalizeText(input && input.value);
    }

    getClickText(marker, target) {
      if (!marker) return '';

      const explicit = this.normalizeText(marker.getAttribute('data-click-text'));
      if (explicit) return explicit;

      let node = target && marker.contains(target) ? target : marker;

      while (node) {
        const value = this.getVisibleText(node);
        if (value) return value;
        if (node === marker) break;
        node = node.parentElement;
      }

      const markerId = marker.getAttribute('id');
      if (markerId) {
        const label = document.querySelector(`label[for="${markerId}"]`);
        const labelText = this.getVisibleText(label);
        if (labelText) return labelText;
      }

      const ariaLabel = this.normalizeText(marker.getAttribute('aria-label'));
      if (ariaLabel) return ariaLabel;

      const image = marker.querySelector('img[alt]');
      return this.normalizeText(image && image.getAttribute('alt'));
    }

    getClickUrl(marker, target) {
      const explicit = this.normalizeText(marker && marker.getAttribute('data-click-url'));
      if (explicit) return explicit;

      const node = target && marker.contains(target) ? target : marker;
      const link = node.closest('a[href]');
      if (!link || (!marker.contains(link) && link !== marker)) return '';

      const href = link.getAttribute('href') || '';
      if (!href || href === '#' || href.indexOf('javascript:') === 0) return '';

      return link.href || href;
    }

    getVisibleText(el) {
      return this.normalizeText(el && el.innerText);
    }

    getVariantRadio(el) {
      if (!el) return null;
      if (el.matches('input[type="radio"]')) return el;

      const label = el.closest('label');
      if (!label) return null;

      const forId = label.getAttribute('for');
      if (forId) return document.getElementById(forId);

      return label.querySelector('input[type="radio"]');
    }

    getVideo(marker) {
      if (marker instanceof HTMLVideoElement) return marker;

      const root = marker.closest('custom-video') || marker;
      return root.querySelector('video');
    }

    getEventTarget(event) {
      if (event.target instanceof Element) return event.target;

      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      for (const node of path) {
        if (node instanceof Element) return node;
      }

      return null;
    }

    getPageContext() {
      return {
        page_path: window.location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      };
    }

    getCountryCode() {
      const shopify = window.Shopify || {};
      return localStorage.getItem('shopCountryCode') || shopify.country || 'US';
    }

    getCurrency() {
      const shopify = window.Shopify || {};
      return (shopify.currency && shopify.currency.active) || 'USD';
    }

    toMoney(value, fromCents) {
      if (value == null || value === '') return 0;

      const raw = String(value).replace(/[^0-9.]/g, '');
      const num = Number(raw);
      if (Number.isNaN(num)) return 0;
      if (fromCents) return Number((num / 100).toFixed(2));
      if (raw.includes('.')) return Number(num.toFixed(2));
      return Number((num / 100).toFixed(2));
    }

    orNa(value) {
      return this.normalizeText(value) || 'NA';
    }

    fillNa(data) {
      if (data == null) return 'NA';
      if (typeof data === 'string') return this.orNa(data);
      if (typeof data !== 'object') return data;

      if (Array.isArray(data)) {
        const items = [];
        for (const item of data) items.push(this.fillNa(item));
        return items;
      }

      const out = {};
      for (const key of Object.keys(data)) out[key] = this.fillNa(data[key]);
      return out;
    }

    normalizeText(value) {
      return String(value || '')
        .replace(/\s+/g, ' ')
        .trim();
    }
  }

  window._theme = window._theme || {};
  window._theme.datalayerTrack = new DatalayerTrack();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window._theme.datalayerTrack.init());
  } else {
    window._theme.datalayerTrack.init();
  }
})();
